<template>
  <div>
    <gl-tree-view
      :disableActions="true"
      :tree-height="200"
      :entries="itemCategories"
      :selected.sync="categories"
      v-on:update:selected="$emit('update:categories', $event)"
    />
    <div class="keyword-search-wrapper">
      <div class="keyword-search">
        <md-field
          md-clearable
          class="keyword-search__keyword-input"
          :class="{ 'md-invalid': noHits }"
        >
          <gl-icon-search />
          <label>検索ワード</label>
          <md-input ref="text" type="text" :value="text" @input="updateText" />
          <span class="md-error">商品が見つかりませんでした</span>
        </md-field>
      </div>
    </div>
    <div class="items-container">
      <item-list
        :items="items"
        :selected.sync="selected"
        category-visible
        selectable
      />
      <div v-if="itemFountExceedsLimit" class="message-max-count-exceeds">
        検索結果上位{{ maxSearchCount }}件を表示しています
      </div>
      <div class="fetching" v-if="fetching">
        <spinner />
      </div>
    </div>
    <div class="button-group">
      <gl-button
        dense
        primary
        icon-pre="list-add"
        class="button-group__button"
        :disabled="items.length === 0"
        @click=";(selected = items), $emit('selected', selected)"
      >
        All
      </gl-button>
      <gl-button
        dense
        primary
        class="button-group__button"
        :disabled="selected.length === 0"
        @click="$emit('selected', selected)"
      >
        <gl-icon-list-add />
      </gl-button>
    </div>
  </div>
</template>

<script>
import { Subject, from, of } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  tap,
  map,
  switchMap,
} from 'rxjs/operators'
import ItemList from '@/components/ItemList.vue'
import Spinner from '@/components/Spinner.vue'

export default {
  components: { ItemList, Spinner },
  props: {
    /**
     * 企業
     */
    company: {
      type: String,
      required: true,
    },
    /**
     * 検索時、最大で表示する商品数
     *
     * 検索結果（Array）の先頭x件を表示します。並び順はリポジトリがどの
     * 順番で返すかに依存します。
     */
    maxSearchCount: {
      type: Number,
      default: 100,
    },
    /**
     * 使用可能カテゴリ
     */
    availableItemCategory: {
      type: Object,
    },
  },
  data() {
    return {
      /**
       * 分類マスタ
       */
      itemCategories: [],
      /**
       * 選択されている分類コード
       *
       * 常に最下層を保存します。
       */
      categories: [],
      /**
       * 検索テキスト
       */
      text: undefined,
      /**
       * 検索テキスト変更Subject
       */
      textSubject: new Subject(),
      /**
       * 検索中
       */
      fetching: false,
      /**
       * 検索テキストでのヒットなし
       */
      noHits: false,
      /**
       * 検索した商品数
       */
      itemFoundCount: 0,
      /**
       * 選択肢に表示する商品
       */
      items: [],
      /**
       * 選択されている商品
       */
      selected: [],
      // /**
      //  * 使用可能カテゴリ
      //  */
      // availableItemCategories: undefined,
    }
  },
  watch: {
    company: {
      immediate: true,
      async handler(company) {
        if (company) {
          // this.availableItemCategories = await this.$repositories
          //   .get('accountAvailableItemCategories')
          //   .getOne(this.$store.state.account.loginId, company)

          // 企業が決まらないと分類は取得できない
          this.itemCategories = await this.$repositories
            .get('itemCategory')
            .get(company, {
              categoryLevel: this.availableItemCategory.categoryLevel,
              categories: this.availableItemCategory.categories,
            })
            .then(categories => [
              {
                code: undefined,
                name: '全分類',
                children: categories,
                open: true,
              },
            ])
        }
      },
    },
  },
  computed: {
    /**
     * 検索した結果が表示最大件数をオーバーしているか
     */
    itemFountExceedsLimit() {
      return this.itemFoundCount > this.maxSearchCount
    },
  },
  methods: {
    /**
     * 検索ワード変更
     */
    updateText(v) {
      this.text = v
      this.textSubject.next(v)
    },
  },
  mounted() {
    // エントリー名絞込のトリガーです
    // 絞込テキストの入力があるとfilterTextSubjectに通知をするので、
    // 適度なタイミング（xxx ms入力が止まっている and 前回と値が違う）で
    // 実際の絞り込み処理を行います。
    const subscription = this.textSubject
      .asObservable()
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          this.fetching = true
        }),
        map(text => text.trim()),
        switchMap(text => {
          if (!text) {
            return of([])
          } else {
            return from(
              this.$repositories
                .get('item')
                .getByKeyword(this.company, this.categories, text, {
                  categoryLevel: this.availableItemCategory.categoryLevel,
                  categories: this.availableItemCategory.categories,
                }),
            )
          }
        }),
      )
      .subscribe(result => {
        this.noHits = result.length === 0
        this.itemFoundCount = result.length
        this.items = result.slice(0, this.maxSearchCount)
        this.selected = []
        this.fetching = false
      })

    this.$on('hook:beforeDestroy', () => subscription.unsubscribe())

    // 入力欄にフォーカスを自動で当てたいが、ダイアログ内にこのコンポーネントを置いた場合、
    // 少し待たないとfocusが効かなかった（$elではちゃんとinput要素が取れていたが）。
    setTimeout(() => {
      this.$refs.text.$el.focus()
    }, 500)
  },
}
</script>

<style lang="scss" scoped>
.items-container {
  height: 200px;
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: 5px;
}

.keyword-search-wrapper {
  margin-top: var(--spacing-medium);
}

.keyword-search {
  width: 100%;
  display: flex;
  align-items: center;

  &__keyword-input {
    flex: 1;
  }
}

.message-max-count-exceeds {
  color: #ff7043; // TODO 色ちゃんとする
  width: 100%;
  text-align: center;
}

.fetching {
  background: rgba($color: #000000, $alpha: 0.2);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10;
}
</style>
